import { MessageCircleMore } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { PredefinedMessagesTableProps } from '@/types/predefined-messages';
import type { VirtualItem } from '@tanstack/react-virtual';

import { EmptyState } from '@/components/common/empty-state';
import { MessageTableRow } from '@/components/configuration-modal/predefined-messages-table/message-table-row';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePredefinedMessageQueriesVirtualizer } from '@/hooks/use-predefined-messages-virtualizer';
import { cn } from '@/lib/utils';

function TableShell({ children, tbodyHeight }: { children: ReactNode; tbodyHeight: number }) {
  const { t } = useTranslation();

  return (
    <Table className="text-event min-h-0 w-full min-w-0 flex-1 table-fixed text-sm">
      <colgroup>
        <col style={{ width: '30%' }} />
        <col style={{ width: '20%' }} />
        <col style={{ width: '20%' }} />
        <col style={{ width: '20%' }} />
        <col style={{ width: '10%' }} />
      </colgroup>
      <TableHeader className="bg-background sticky top-0 z-10">
        <TableRow className="border-train-stroke **:text-foreground **:font-medium hover:bg-transparent">
          <TableHead className="px-5">
            {t('common:configuration.predefinedMessages.columns.message')}
          </TableHead>
          <TableHead>{t('common:configuration.predefinedMessages.columns.category')}</TableHead>
          <TableHead>{t('common:configuration.predefinedMessages.columns.availableTo')}</TableHead>
          <TableHead>{t('common:configuration.predefinedMessages.columns.recipients')}</TableHead>
          <TableHead className="px-5 text-right">
            {t('common:configuration.predefinedMessages.columns.actions')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody style={{ position: 'relative', height: tbodyHeight }}>{children}</TableBody>
    </Table>
  );
}

export function PredefinedMessagesTable({
  filteredMessages,
  categoryNames,
  recipients,
  className,
  onEdit,
  onDelete,
}: PredefinedMessagesTableProps) {
  const { t } = useTranslation();

  const messages = filteredMessages ?? [];

  const { parentRef, rowVirtualizer } = usePredefinedMessageQueriesVirtualizer(messages);

  const virtualItems = rowVirtualizer.getVirtualItems();
  const tbodyHeight = rowVirtualizer.getTotalSize();

  if (!messages.length) {
    return (
      <EmptyState
        icon={<MessageCircleMore className="text-chat-cross" />}
        title={t('common:configuration.predefinedMessages.emptyTitle')}
        subtitle={t('common:configuration.predefinedMessages.emptyDescription')}
      />
    );
  }

  return (
    <div
      ref={parentRef}
      className={cn(
        'border-separator scrollbar-custom flex flex-1 flex-col overflow-y-auto',
        className
      )}
    >
      <TableShell tbodyHeight={tbodyHeight}>
        {virtualItems.map((virtualRow: VirtualItem) => {
          const filteredMessage = messages[virtualRow.index];

          return (
            <MessageTableRow
              key={filteredMessage.id}
              filteredMessage={filteredMessage}
              categoryNames={categoryNames}
              recipients={recipients}
              dataIndex={virtualRow.index}
              onEdit={onEdit}
              onDelete={onDelete}
              className="border-train-stroke hover:bg-transparent"
            />
          );
        })}
      </TableShell>
    </div>
  );
}
